<template>
  <CoreLayout :style="colorVarStyles">
    <LpuStaffAdmin />
    <CoreBread
      v-if="!isMobile"
      :items="breadcrumbs"
    />
    <div
      data-qa="lpu_page_price"
      class="lpu-branding container position-relative"
      :class="{
        'lpu-branding--mobile pt-4': isMobile,
        'lpu-branding--phone-bar': isMobile && appointmentPhone && !isSpecialistsOnly,
        'mb-5': !isMobile,
      }"
      itemtype="https://schema.org/LocalBusiness"
      itemscope
    >
      <meta
        itemprop="name"
        :content="tDynamic(context.lpu, 'name')"
      >
      <meta
        v-if="localBusinessItemprops.telephone"
        itemprop="telephone"
        :content="localBusinessItemprops.telephone"
      >
      <span
        v-if="localBusinessItemprops.addressLocality || localBusinessItemprops.streetAddress"
        itemprop="address"
        itemscope
        itemtype="https://schema.org/PostalAddress"
      >
        <meta
          v-if="localBusinessItemprops.addressLocality"
          itemprop="addressLocality"
          :content="localBusinessItemprops.addressLocality"
        >
        <meta
          v-if="localBusinessItemprops.streetAddress"
          itemprop="streetAddress"
          :content="localBusinessItemprops.streetAddress"
        >
      </span>
      <meta
        v-if="localBusinessItemprops.priceRange"
        itemprop="priceRange"
        :content="localBusinessItemprops.priceRange"
      >
      <meta
        v-if="localBusinessItemprops.logo"
        itemprop="logo"
        :content="localBusinessItemprops.logo"
      >
      <PreviewMenu
        v-if="context.request.feature_flags.ff_lpu_branding_page_v2 && context.is_lpu_branding_preview"
        class="order-first"
        :lpu-id="context.lpu.id"
      />
      <div class="d-flex flex-column ga-6">
        <HeroSectionDesktop
          v-if="!isMobile"
          :lpu="context.lpu"
          :lpu-branding="context.lpu_branding"
          :lpu-video-card="context.lpu_video_card"
          :synonym-prices-count="context.synonym_prices_count"
        />
        <SectionMenu
          v-if="context.request.feature_flags.ff_lpu_branding_page_v2"
          active-item-id="prices"
          :lpu="context.lpu"
          :synonym-prices-count="context.synonym_prices_count"
          :lpu-page-href="lpuPageHref"
        />
      </div>
      <PriceLpuBack :lpu="context.lpu" />
      <component :is="PriceSection" />
      <PricePhoneBar
        v-if="isMobile && !isSpecialistsOnly"
        :phone="appointmentPhone"
        :href="appointmentPhoneHref"
      />
      <InvasiveServiceInfo v-if="isClientMounted" />
      <CoreSnackbar />
      <meta
        itemprop="url"
        :content="context.lpu.site || lpuPageHref"
      >
    </div>
    <BreadcrumbsJsonLd
      v-if="isMobile"
      :json="breadcrumbsJsonLd"
    />
    <BreadcrumbsJsonLd
      :json="localBusinessJsonLd"
    />
    <LpuPopupMap />
  </CoreLayout>
</template>

<script lang="ts" setup>
import {
  computed,
  nextTick,
  onMounted,
  ref,
} from 'vue'
import { CoreLayout } from 'src/layouts'
import CoreBread from 'src/core/CoreBread/CoreBread.vue'
import BreadcrumbsJsonLd from 'src/core/CoreBread/BreadcrumbsJsonLd.vue'
import CoreSnackbar from 'src/core/CoreSnackbar/CoreSnackbar.vue'
import useIsMobile from 'src/composables/useIsMobile'
import useContextStore from 'src/store/useContextStore'
import useURLStore from 'src/store/useURLStore'
import InvasiveServiceInfo from 'src/components/common/InvasiveServiceInfo/InvasiveServiceInfo.vue'
import LpuPopupMap from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPopupMap.vue'
import sendYaGoal from 'src/utils/sendYaGoal'
import smoothScrollToSelector from 'src/utils/smoothScrollToSelector'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import getLocalBusinessItemprops, {
  buildLocalBusinessJsonLd,
  getLocalBusinessRatingValue,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/getLocalBusinessItemprops'
import useBrandingColors from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_composables/useBrandingColors'
import LpuStaffAdmin from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuStaffAdmin/LpuStaffAdmin.vue'
import PreviewMenu from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/PreviewMenu/PreviewMenu.vue'
import SectionMenu from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/SectionMenu.vue'
import type { ContextStore } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/types'
import { PRICE_PAGE_GOAL } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/constants'
import usePriceAppointmentPhone from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/composables/usePriceAppointmentPhone'
import { isInitialSpecialistsOnlyPrice } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/composables/usePriceCatalog'
import PriceLpuBack from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceLpuBack.vue'
import PricePhoneBar from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PricePhoneBar.vue'

const { context }: ContextStore = useContextStore()

const { reverseFull } = useURLStore()
const isMobile = useIsMobile()
const { colorVarStyles } = useBrandingColors(computed(() => context.lpu_branding))
const { appointmentPhone, appointmentPhoneHref } = usePriceAppointmentPhone(context.lpu)
const isSpecialistsOnly = computed(() => isInitialSpecialistsOnlyPrice(context))

const isClientMounted = ref(false)
const lpuPageHref = reverseFull({
  name: 'lpu',
  args: [context.town.translit, context.lpu.translit],
})
/** telephone / address / priceRange / logo — паритет с legacy LocalBusiness. */
const localBusinessItemprops = computed(() => getLocalBusinessItemprops({
  lpu: context.lpu,
  townName: tDynamic(context.town, 'name'),
  addressText: context.lpu.addr?.[0]
    ? tDynamic(context.lpu.addr[0], 'address')
    : undefined,
  priceRange: context.price_range,
  currencyAbbreviation: context.currency?.abbreviation,
  country: context.request.country,
  httpHost: context.request.http_host,
  pageUrl: lpuPageHref,
  ffUpdateLpuLogo: Boolean(context.request.feature_flags.ff_update_lpu_logo),
  tStatic,
}))
const itempropUrl = computed(() => context.lpu.site || lpuPageHref)
const localBusinessJsonLd = computed(() => JSON.stringify(buildLocalBusinessJsonLd({
  name: tDynamic(context.lpu, 'name') || context.lpu.name,
  url: itempropUrl.value,
  itemprops: localBusinessItemprops.value,
  ratingValue: context.lpu.allrates > 0
    ? getLocalBusinessRatingValue(context.lpu.stars)
    : undefined,
  ratingCount: context.lpu.allrates,
})).replace(/</g, '\\u003c'))
const breadcrumbs = computed(() => {
  const items = [
    {
      name: context.request.country.is_russia ? 'ПроДокторов' : 'YDoc',
      href: reverseFull({ name: 'prodoctorov_main' }),
    },
    {
      name: tDynamic(context.town, 'name'),
      href: reverseFull({ name: 'town', args: [context.town.translit] }),
    },
    {
      name: tStatic('Лечебные учреждения'),
      href: reverseFull({ name: 'town_lpu', args: [context.town.translit] }),
    },
  ]

  if (!context.lpu.closed) {
    const typeNames = tDynamic(context.lpu.lputype, 'names')
    const typeName = typeNames
      ? typeNames.charAt(0).toUpperCase() + typeNames.slice(1)
      : ''

    items.push({
      name: typeName,
      href: reverseFull({
        name: 'lpulist',
        args: [context.town.translit, context.lpu.lputype.translit],
      }),
    })
  }

  items.push(
    {
      name: tDynamic(context.lpu, 'name') || context.lpu.name,
      href: lpuPageHref,
    },
    {
      name: tStatic('Цены'),
      href: reverseFull({
        name: 'lpu_price',
        args: [context.town.translit, context.lpu.translit],
      }),
    },
  )

  return items
})
/**
 * JSON-LD хлебных крошек для mobile (как на главной брендированной странице ЛПУ).
 * Визуальные крошки на mobile не показываются.
 */
const breadcrumbsJsonLd = computed(() => JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbs.value.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.href,
  })),
}).replace(/</g, '\\u003c'))

const HeroSectionDesktop = () => import(/* webpackMode: 'eager' */ 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/HeroSection/HeroSectionDesktop.vue')

const PriceSectionDesktop = () => import(/* webpackMode: 'eager' */ 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/PriceSectionDesktop.vue')
const PriceSectionMobile = () => import(/* webpackMode: 'eager' */ 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/PriceSectionMobile.vue')
const PriceSection = computed(() => (isMobile.value ? PriceSectionMobile : PriceSectionDesktop))

onMounted(() => {
  isClientMounted.value = true
  sendYaGoal(PRICE_PAGE_GOAL.VISIT)

  const hash = window.location.hash.replace('#', '')

  if (!hash) {
    return
  }

  // PriceSection — async import: якорь может появиться после первого mount.
  const tryScroll = (attemptsLeft: number) => {
    nextTick()
      .then(() => {
        if (document.getElementById(hash)) {
          smoothScrollToSelector(`#${hash}`)
          return
        }

        if (attemptsLeft <= 1) {
          return
        }

        requestAnimationFrame(() => tryScroll(attemptsLeft - 1))
      })
      .catch(() => undefined)
  }

  tryScroll(10)
})
</script>

<style lang="scss">
@import 'src/styles/index';
@import 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_styles/colors';
@import 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_styles/rounded';
@import 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_styles/branding-btn';

.lpu-branding {
  display: flex;
  flex-direction: column;
  row-gap: 32px;

  &--mobile {
    row-gap: 32px;
  }

  &--phone-bar {
    padding-bottom: 76px;
  }
}
</style>
